import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import IDCard from './IDCard.jsx';
import { toPng } from 'html-to-image';
import { requestFirebaseNotificationPermission, onMessageListener } from '../firebase.js';

function UserDashboard() {
  const { user, logout, checkAuthStatus } = useAuth();
  const navigate = useNavigate();
  const idCardRef = useRef(null);

  // Fetch the full user details on mount so the ID card has all the required data
  // (blood group, DOB, emergency contact are not in the basic JWT)
  useEffect(() => {
    checkAuthStatus();

    // Request FCM Token and send to backend
    const initializeFirebase = async () => {
      try {
        const token = await requestFirebaseNotificationPermission();
        if (token && user?.subscriptionStatus === 'active') {
          // Send token to our server
          await fetch('https://healthcard.searchisend.com/health_api/update_fcm_token.php', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ token: token })
          });
        }
      } catch (err) {
        console.error("Failed to setup FCM:", err);
      }
    };

    initializeFirebase();

    // Listen for foreground messages
    onMessageListener().then(payload => {
      console.log("Foreground push received:", payload);
      // Optional: Show a custom toast/alert here
    }).catch(err => console.log('failed: ', err));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.subscriptionStatus]);

  const handleDownload = async () => {
    if (idCardRef.current === null) {
      return;
    }
    try {
      const options = {
        pixelRatio: 4, // Higher pixel ratio for much sharper ID card images
        cacheBust: true,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        fontEmbedCSS: '', // Bypass Google Fonts CORS error in html-to-image
      };
      const dataUrl = await toPng(idCardRef.current, options);
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `Health Card-ID-Card-${user.memberId}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Oops, something went wrong!', err);
      alert('Could not download the ID card. This may be due to a browser security issue. Please try again.');
    }
  };

  const handleCopyLink = () => {
    const profileUrl = `https://healthcard.searchisend.com/member/${user?.memberId}`;
    navigator.clipboard.writeText(profileUrl)
      .then(() => alert('Profile Link copied to clipboard!'))
      .catch(() => alert('Failed to copy link. Your link is: ' + profileUrl));
  };

  return (
    <div className="glass p-8 md:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] w-full max-w-4xl border border-white/50 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
        <div>
          <h1 className="text-4xl font-extrabold text-brand-secondary tracking-tight">Welcome, <span className="text-brand-primary">{user?.firstName}</span>!</h1>
          <p className="text-slate-500 font-medium mt-1">Manage your digital health profile and credentials.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <button onClick={() => window.open(`https://healthcard.searchisend.com/member/${user?.memberId}`, '_blank')} className="flex-1 md:flex-none bg-brand-accent hover:bg-orange-600 text-white font-bold py-2.5 px-5 rounded-full shadow-md shadow-orange-600/20 transition-all transform hover:-translate-y-0.5 whitespace-nowrap flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
            Live Profile
          </button>
          <button onClick={() => navigate('/edit-profile')} className="flex-1 md:flex-none bg-brand-primary hover:bg-emerald-700 text-white font-bold py-2.5 px-5 rounded-full shadow-md shadow-emerald-700/20 transition-all transform hover:-translate-y-0.5 whitespace-nowrap">
            Edit Profile
          </button>
          <button onClick={() => navigate('/change-password')} className="flex-1 md:flex-none glass text-brand-secondary font-bold py-2.5 px-5 rounded-full hover:bg-white/80 transition-all shadow-sm whitespace-nowrap">
            Password
          </button>
          <button onClick={logout} className="flex-1 md:flex-none bg-red-50 hover:bg-red-100 text-red-600 font-bold py-2.5 px-5 rounded-full transition-colors whitespace-nowrap border border-red-200 shadow-sm">
            Logout
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-10 items-center md:items-start bg-white/40 p-6 md:p-8 rounded-2xl border border-white/60">

        {user?.subscriptionStatus === 'active' ? (
          <>
            <div className="md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left">
              <div className="w-12 h-12 bg-emerald-100 text-brand-primary rounded-xl flex items-center justify-center mb-4">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"></path></svg>
              </div>
              <h2 className="text-2xl font-bold text-brand-secondary mb-3">Your Digital ID Card</h2>
              <p className="text-slate-500 font-medium mb-4 leading-relaxed">
                Download your official health card. Keep it on your phone or print it out. In an emergency, first responders can scan the QR code to access your critical medical information instantly.
              </p>
              <div className="flex flex-col w-full gap-3 mt-8">
                <button onClick={handleDownload} className="w-full bg-brand-accent hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-full shadow-[0_4px_14px_0_rgb(234,88,12,0.39)] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>
                  Download Print-Ready ID Card
                </button>

                <button onClick={handleCopyLink} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-8 rounded-full transition-colors flex items-center justify-center gap-2 border border-slate-300">
                  <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>
                  Copy Live Profile Link
                </button>
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center w-full">
              <IDCard user={user} ref={idCardRef} />
            </div>
          </>
        ) : (
          <div className="w-full text-center py-12 px-4">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-iner border-4 border-white">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
            </div>
            <h2 className="text-3xl font-extrabold text-brand-secondary mb-3">Subscription Inactive</h2>
            <p className="text-gray-500 font-medium mb-8 max-w-lg mx-auto text-lg">
              Your Health Card subscription is currently inactive. You must activate or renew your subscription to access your Digital ID Card and allow emergency responders to view your medical profile.
            </p>
            <button
              onClick={() => navigate('/checkout')}
              className="bg-brand-primary hover:bg-emerald-700 text-white font-bold py-4 px-10 rounded-full text-lg shadow-xl shadow-emerald-700/30 transition-all transform hover:-translate-y-1"
            >
              Activate Subscription Now
            </button>
          </div>
        )}

      </div>

      {/* Subscription Management Tile */}
      {user?.subscriptionStatus === 'active' && (
        <div className="mt-8 bg-white p-6 md:p-8 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-center gap-6 animate-fade-in-up delay-100">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-blue-50 border border-blue-100 text-blue-600 rounded-full flex items-center justify-center shrink-0">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
            </div>
            <div>
              <h3 className="text-xl font-extrabold text-brand-secondary mb-1">Membership Active</h3>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 font-medium">Current Plan:</span>
                <span className="px-2.5 py-0.5 rounded text-xs font-bold bg-emerald-100 text-emerald-800 uppercase tracking-wide">
                  {user?.planName || 'Standard Plan'}
                </span>
              </div>
              <p className="text-sm text-slate-400 mt-1.5 flex items-center gap-1.5">
                <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Valid until: <strong className="text-slate-600 font-bold">{new Date(user?.subscriptionExpiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/checkout')} className="w-full md:w-auto bg-brand-secondary hover:bg-slate-800 text-white font-bold py-3 px-8 rounded-xl shadow-md transition-all whitespace-nowrap flex items-center justify-center gap-2 transform hover:-translate-y-0.5">
            Renew Subscription
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
          </button>
        </div>
      )}

    </div>
  );
}

export default UserDashboard;

